import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';

export type TCustomer = components['schemas']['CustomerResDto'];
export type TCustomerSource = components['schemas']['CustomerSourceResDto'];
export type TCustomerSecret = components['schemas']['CustomerSecretResDto'];
export type TUpsertedCustomerSecret = components['schemas']['UpsertedCustomerSecretResDto'];
export type TCustomerUpdate = components['schemas']['UpdateCustomerReqDto'];
export type TCustomerActivity = TCustomer['activity'];

export const listCustomers = () =>
  unwrap(api.GET('/v1/customers', { params: { query: { limit: 100, withTotal: true } } }));

export const createCustomer = (body: { name: string; externalId: string }): Promise<TCustomer> =>
  unwrap(api.POST('/v1/customers', { body }));

export const updateCustomer = (id: string, body: TCustomerUpdate): Promise<TCustomer> =>
  unwrap(api.PATCH('/v1/customers/{id}', { params: { path: { id } }, body }));

export const customerSources = (id: string): Promise<TCustomerSource[]> =>
  unwrap(api.GET('/v1/customers/{id}/sources', { params: { path: { id } } }));

export const attachCustomerSource = (id: string, sourceId: string) =>
  unwrap(api.POST('/v1/customers/{id}/sources', { params: { path: { id } }, body: { sourceId } }));

export type TSourceCustomer = components['schemas']['SourceCustomerResDto'];

export const SOURCE_CUSTOMERS_PAGE = 50;

// One prefix per source, so a change to any client refreshes the list and every open page.
export const sourceClientKeys = {
  everywhere: ['source-clients'] as const,
  all: (sourceId: string) => ['source-clients', sourceId] as const,
  list: (sourceId: string, q: string) => ['source-clients', sourceId, 'list', q] as const,
  one: (sourceId: string, customerId: string) =>
    ['source-clients', sourceId, 'one', customerId] as const,
};

export const sourceCustomers = (sourceId: string, query: { q?: string; cursor?: string } = {}) =>
  unwrap(
    api.GET('/v1/sources/{id}/customers', {
      params: {
        path: { id: sourceId },
        query: { limit: SOURCE_CUSTOMERS_PAGE, withTotal: true, ...query },
      },
    }),
  );

export type TAddSourceCustomer = components['schemas']['AddSourceCustomerReqDto'];

export const sourceCustomer = (sourceId: string, customerId: string): Promise<TSourceCustomer> =>
  unwrap(
    api.GET('/v1/sources/{id}/customers/{customerId}', {
      params: { path: { id: sourceId, customerId } },
    }),
  );

export const addSourceCustomer = (
  sourceId: string,
  body: TAddSourceCustomer,
): Promise<TSourceCustomer> =>
  unwrap(api.POST('/v1/sources/{id}/customers', { params: { path: { id: sourceId } }, body }));

// Soft: the address row survives, so attaching again hands back the identical URL.
export const detachCustomerSource = async (id: string, sourceId: string): Promise<void> => {
  await unwrap(
    api.DELETE('/v1/customers/{id}/sources/{sourceId}', { params: { path: { id, sourceId } } }),
  );
};

export const disableCustomer = (id: string): Promise<TCustomer> =>
  unwrap(api.POST('/v1/customers/{id}/disable', { params: { path: { id } } }));

export const enableCustomer = (id: string): Promise<TCustomer> =>
  unwrap(api.POST('/v1/customers/{id}/enable', { params: { path: { id } } }));

// No `value`: the server generates one and returns it in `secret.plaintext`, this once.
export const upsertCustomerSecret = (
  id: string,
  sourceId: string,
  body: { value?: string; graceHours?: number; authMethodId?: string },
): Promise<TUpsertedCustomerSecret> =>
  unwrap(
    api.POST('/v1/customers/{id}/sources/{sourceId}/secrets', {
      params: { path: { id, sourceId } },
      body,
    }),
  );
